import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  FormHelperText,
  Link
} from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as XLSX from 'xlsx';

import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import PageTitle from 'src/components/PageTitle';
import { deleteEntry, get, post, put, multy_post } from 'src/services/httpClient.service';
import { authenticationService } from 'src/services/authentication.service';

// Import the StateType interface
export interface StateType {
  id: number;
  country_id: number;
  name: string;
  shortCode?: string;
  isActive: boolean;
  vendorId: string;
}

const ImportsDataFiles = () => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [fileType, setFileType] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedStateName, setSelectedStateName] = useState('');
  const [states, setStates] = useState<StateType[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [tableLoading, setTableLoading] = useState(false);

  const { vendorId, franchiseId, companyId, operatorId } = authenticationService.getVendorFranchiseIds();
  const APIURL = process.env.REACT_APP_BASE_API_URL;

  const importConfig = {
    SUBSCRIBER: {
      title: 'Bulk Upload Subscribers',

      mandatoryFields: [
        'Name',
        'Phone',
        'LCO',
        'Installation Address',
        'Billing Address'
      ],

      sampleHeaders: [
        'Subscriber Name',
        'Email',
        'Phone',
        'User Name',
        'Password',
        'LCO',
        'Installation Address',
        'Billing Address',
        'Package',
        'Start Date',
        'End Date'
      ],

      sampleFileName: 'Subscriber_Sample.csv'
    },

    LCO: {
      title: 'Bulk Upload LCOs',

      mandatoryFields: [
        'Company ID',
        'Role IDs List',
        'Operator Name',
        'Account Type',
        'Distributor ID',
        'Email',
        'Password',
        'Phone',
        'Address',
        'Landmark/Village',
        'Area ID',
        'LCO ID',
        'Vendor ID',
        'Enable Free Access',
        'Allow Low Balance Recharge',
        'Status'
      ],

      sampleHeaders: [
        'companyId',
        'roleIdsList',
        'operatorName',
        'accountType',
        'distributorId',
        'email',
        'password',
        'phone',
        'address1',
        'address2',
        'areaId',
        'lcoId',
        'vendorId',
        'enableFreeAccess',
        'allowLowBalanceRecharge',
        'status'
      ],

      sampleFileName: 'LCO_Sample.xlsx'
    },

    CHANNEL: {
      title: 'Bulk Upload Channels',

      mandatoryFields: [
        'BROADCASTER',
        'STREAM_URL',
        'STREAM_PORT',
        'STREAM_TYPE',
        'CATEGORY',
        'LANGUAGE',
        'CHANNEL_NAME',
        'CHANNEL_URL',
        'UDP',
        'AUDIO_PID',
        'PROGRAMME_PID',
        'VIDEO_PID',
        'LCN',
        'IS_PAID_CHANNEL',
        'IS_LANDING_CHANNEL',
        'PRICE',
        'LOCAL_ADDRESS',
        'BANNER',
        'IMAGE'
      ],

      sampleHeaders: [
        'BROADCASTER',
        'STREAM_URL',
        'STREAM_PORT',
        'STREAM_TYPE',
        'CATEGORY',
        'LANGUAGE',
        'CHANNEL_NAME',
        'CHANNEL_URL',
        'UDP',
        'AUDIO_PID',
        'PROGRAMME_PID',
        'VIDEO_PID',
        'LCN',
        'IS_PAID_CHANNEL',
        'IS_LANDING_CHANNEL',
        'PRICE',
        'LOCAL_ADDRESS',
        'BANNER',
        'IMAGE'
      ],

      sampleFileName: 'channel_sample.xlsx'
    },
    LOCATION: {
      title: 'Bulk Upload Locations',

      mandatoryFields: [
        'COUNTRY',
        'STATE',
        'DISTRICT',
        'CITY',
        'VILLAGE/AREA',
        'PIN_CODE'
      ],

      sampleHeaders: [
        'COUNTRY',
        'STATE',
        'DISTRICT',
        'CITY',
        'VILLAGE/AREA',
        'PIN_CODE'
      ],

      sampleFileName: 'locations.xlsx'
    }
  };

  // Fetch states from API
  const fetchStates = async () => {
    try {
      setStatesLoading(true);
      const response = await get(
        `${APIURL}/api/miscellaneous/state/vendor/${vendorId}/findAll`
      );
      console.log('States response:', response);
      if (response && response.data) {
        setStates(response.data);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setStatesLoading(false);
    }
  };

  // Fetch states when dialog opens and only if location is selected
  useEffect(() => {
    if (openAddDialog && fileType === 'LOCATION') {
      fetchStates();
    }
  }, [openAddDialog, fileType]);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    // Reset state selection when dialog opens
    setSelectedState('');
    setSelectedStateName('');
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFileType('');
    setSelectedState('');
    setSelectedStateName('');
    setSelectedFile(null);
    setUploadErrors([]);
  };

  const handleFileTypeChange = (value: string) => {
    setFileType(value);
    setSelectedFile(null);
    setUploadErrors([]);
    // Reset state selection when file type changes
    setSelectedState('');
    setSelectedStateName('');
    
    // If location is selected, fetch states
    if (value === 'LOCATION') {
      fetchStates();
    }
  };

  const handleStateChange = (stateId: string) => {
    setSelectedState(stateId);
    // Find the selected state name
    const selectedStateObj = states.find(state => state.id === Number(stateId));
    if (selectedStateObj) {
      setSelectedStateName(selectedStateObj.name);
    } else {
      setSelectedStateName('');
    }
    // Clear state-related errors when selection changes
    setUploadErrors(prev => prev.filter(err => !err.includes('state')));
  };

  const handleFile = (file: File | null) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadErrors(['Only .csv files are allowed']);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setUploadErrors([]);
  };

  const downloadSampleFile = () => {
    if (!fileType) return;

    const config = importConfig[fileType];

    if (!config) return;

    const worksheet = XLSX.utils.aoa_to_sheet([
      config.sampleHeaders
    ]);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      fileType
    );

    XLSX.writeFile(
      workbook,
      config.sampleFileName
    );
  };

  // Function to download state-specific Excel file
  const downloadStateExcel = () => {
    if (!selectedStateName) return;

    // Construct the path to the state Excel file
    // The file should be named exactly as the state name with .xlsx extension
    const fileName = `${selectedStateName}.xlsx`;
    const filePath = `/static/images/states/${fileName}`;
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!fileType || !selectedFile) {
      setUploadErrors(['Please select file type and file']);
      return;
    }

    // Validate state selection only for LOCATION file type
    if (fileType === 'LOCATION' && !selectedState) {
      setUploadErrors(['Please select a state for location upload']);
      return;
    }

    setUploadLoading(true);
    setUploadErrors([]);

    try {
      console.log('Selected File Type:', fileType);
      console.log('Selected File:', selectedFile);
      console.log('Selected State:', selectedState);
      console.log('Selected State Name:', selectedStateName);

      const formData = new FormData();

      formData.append('file', selectedFile);
      formData.append('fileType', fileType);
      formData.append('vendorId', vendorId);
      
      // Only append stateId if location is selected
      if (fileType === 'LOCATION' && selectedState) {
        formData.append('stateId', selectedState);
        formData.append('stateName', selectedStateName);
      }

      const response = await multy_post(`${APIURL}/api/import/${fileType}/upload`, formData);

      console.log('FormData ready:', formData);
      console.log('response:', response);

      fetchImportData();

      // Close after successful API response
      handleCloseAddDialog();

    } catch (error) {
      console.error('Upload error:', error);

      setUploadErrors([
        error?.message || 'Upload failed'
      ]);
    } finally {
      setUploadLoading(false);
    }
  };

  const fetchImportData = async () => {
    try {
      setTableLoading(true);

      const response = await get(
        `${APIURL}/api/import/vendor/${vendorId}/findAll`
      );

      console.log('GET API Response:', response);

      setRows(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch import data:', error);
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchImportData();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <PageTitleWrapper>
        {hasPermission(['OWNER', 'importDataFiles.create']) && (
          <PageTitle
            heading="Imports Data Files"
            subHeading="Import LCOs, Subscribers, Channels Data"
            handleClick={handleOpenAddDialog}
            label="Upload"
          />
        )}
      </PageTitleWrapper>

      {/* TABLE */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>ID</strong>
              </TableCell>

              <TableCell>
                <strong>File Type</strong>
              </TableCell>

              <TableCell>
                <strong>State</strong>
              </TableCell>

              <TableCell align="center">
                <strong>Total Count</strong>
              </TableCell>

              <TableCell align="center">
                <strong>Success Count</strong>
              </TableCell>

              <TableCell align="center">
                <strong>Failure Count</strong>
              </TableCell>

              <TableCell align="center">
                <strong>Status</strong>
              </TableCell>

              <TableCell>
                <strong>Success File</strong>
              </TableCell>

              <TableCell>
                <strong>Failure File</strong>
              </TableCell>

              <TableCell>
                <strong>Done By</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {tableLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((row, index) => (
                <TableRow key={row.id || index} hover>
                  <TableCell>
                    {row.id}
                  </TableCell>

                  <TableCell>
                    {row.importType}
                  </TableCell>

                  <TableCell>
                    {row.stateName || '-'}
                  </TableCell>

                  <TableCell align="center">
                    {row.totalCount}
                  </TableCell>

                  <TableCell align="center">
                    {row.successCount}
                  </TableCell>

                  <TableCell align="center">
                    {row.failureCount}
                  </TableCell>

                  <TableCell align="center">
                    {row.status}
                  </TableCell>

                  <TableCell>
                    {row.successFileUrl ? (
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        component="a"
                        href={row.successFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  <TableCell>
                    {row.failureFileUrl ? (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DownloadIcon />}
                        component="a"
                        href={row.failureFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  <TableCell>
                    {row.createdByName}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No import records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* UPLOAD DIALOG */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Import Data Files
        </DialogTitle>

        <DialogContent>
          {/* FILE TYPE DROPDOWN */}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="file-type-label">
              File Type
            </InputLabel>

            <Select
              labelId="file-type-label"
              value={fileType}
              label="File Type"
              onChange={(e) =>
                handleFileTypeChange(e.target.value as string)
              }
            >
              <MenuItem value="SUBSCRIBER">
                Subscribers
              </MenuItem>

              <MenuItem value="LCO">
                LCOs
              </MenuItem>

              <MenuItem value="LOCATION">
                Locations
              </MenuItem>
            </Select>
          </FormControl>

          {/* STATE DROPDOWN - Only visible when LOCATION is selected */}
          {fileType === 'LOCATION' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }} error={uploadErrors.some(err => err.includes('state'))}>
                <InputLabel id="state-select-label">
                  Select State *
                </InputLabel>

                <Select
                  labelId="state-select-label"
                  value={selectedState}
                  label="Select State *"
                  onChange={(e) => handleStateChange(e.target.value as string)}
                  disabled={statesLoading}
                >
                  {statesLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : states.length === 0 ? (
                    <MenuItem disabled>
                      No states available
                    </MenuItem>
                  ) : (
                    states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))
                  )}
                </Select>

                {uploadErrors.some(err => err.includes('state')) && (
                  <FormHelperText>
                    {uploadErrors.find(err => err.includes('state'))}
                  </FormHelperText>
                )}
              </FormControl>

              {/* Download State Excel Link - Shows only when a state is selected */}
              {selectedState && selectedStateName && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={downloadStateExcel}
                    sx={{ 
                      width: '100%',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    Download {selectedStateName} Excel File
                  </Button>
                </Box>
              )}
            </>
          )}

          {/* DYNAMIC CONTENT */}
          {fileType && importConfig[fileType] && (
            <>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  fontWeight: 600
                }}
              >
                {importConfig[fileType].title}
              </Typography>

              {/* MANDATORY FIELDS */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1 }}
                >
                  Mandatory Fields
                </Typography>

                <Typography
                  sx={{
                    color: 'error.main',
                    fontSize: '16px'
                  }}
                >
                  {importConfig[fileType].mandatoryFields.join(', ')}
                </Typography>
              </Box>

              {/* DRAG & DROP */}
              <Box
                sx={{
                  border: '2px dashed #aaa',
                  padding: '30px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: '4px',

                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();

                  const file = e.dataTransfer.files[0];

                  handleFile(file);
                }}
                onClick={() => {
                  document
                    .getElementById('importFileInput')
                    ?.click();
                }}
              >
                <Typography>
                  Drag & Drop CSV File OR Click to Upload
                </Typography>

                <input
                  id="importFileInput"
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={(e) => {
                    handleFile(
                      e.target.files?.[0] || null
                    );

                    e.target.value = '';
                  }}
                />
              </Box>

              {/* SELECTED FILE */}
              {selectedFile && (
                <Typography
                  sx={{
                    mt: 2,
                    color: 'success.main'
                  }}
                >
                  Selected File: {selectedFile.name}
                </Typography>
              )}

              {/* ERRORS */}
              {uploadErrors.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    color: 'error.main'
                  }}
                >
                  {uploadErrors.map((error, index) => (
                    <Typography key={index}>
                      {error}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* DOWNLOAD SAMPLE */}
              <Button
                startIcon={<DownloadIcon />}
                sx={{ mt: 2 }}
                onClick={downloadSampleFile}
              >
                Download Sample File
              </Button>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseAddDialog}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={
              !fileType ||
              !selectedFile ||
              (fileType === 'LOCATION' && !selectedState) ||
              uploadLoading ||
              statesLoading
            }
            onClick={handleUpload}
          >
            {uploadLoading ? (
              <CircularProgress
                size={20}
                color="inherit"
              />
            ) : (
              'Import'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportsDataFiles;